import React, { Component } from 'react';
import classnames from 'classnames';
import {
	TableRow,
	TableCell,
	TableContainer,
	Table,
	TableHead,
	TableBody,
	Pagination
} from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import APCell from './APCell';
import APTradeRows from './APTradeRows';
import { generate as xslx } from './utils/xlsx';
import { Series, SeriesField, CommissionViewType } from './../../../core/types/Series';
import { withTranslate } from '../../../core/TenantProvider';
import APSeriesTableStyles from './styles/APSeriesTable.style';
import APSelectDropdown from '../../atoms/APSelectDropdown';
import EmptyCommissionIcon from './assets/icons/empty_commission.svg';
import {
	ALL_COMMISSION_PAID_TYPE,
	AUTO_COMMISSION_PAID_TYPE,
	COMMISSIONS_DETAILED_VIEW,
	COMMISSIONS_SUMMARY_VIEW,
	MANUAL_COMMISSION_PAID_TYPE
} from '../../../core/utils/Constants/Constants';

const StyledTableRow = withStyles((theme) => ({
	root: {
		backgroundColor: '#ffffff',
		'&:nth-of-type(odd)': {
			backgroundColor: '#F5F5F5',
		},
		'&.isResponsive': {
			display: 'block',
			width: 'auto',
			borderBottom: '1px solid rgba(224, 224, 224, 1)',
			padding: '1.25em',
			position: 'relative',
		}
	},
}))(TableRow);

interface APSeriesTableProps extends WithStyles<typeof APSeriesTableStyles> {
	hideEvent?: boolean,
	pagination?: number,
	summaryHeader?: string,
	summaryHeaderBrackets?: string,
	colSpanCount?: number,
	dateColumnCopy?: string,
	series: Array<Series> | null,
	translate: Function,
	breakPoint?: any,
	ignoredFields?: any,
	referralsSearchEnable?: boolean,
	referralSearchString?: string,
	forInvoice?: boolean
}

class APSeriesTable extends Component<APSeriesTableProps, {}> {
	table: any;

	state = {
		page: 1,
		commissionType: ALL_COMMISSION_PAID_TYPE,
		filteredSeries: null
	};

	constructor(props: APSeriesTableProps) {
		super(props);

		this.onChangePage = this.onChangePage.bind(this);
		this.onClick = this.onClick.bind(this);
		this.NormalRows = this.NormalRows.bind(this);
		this.NoCommissionDataView = this.NoCommissionDataView.bind(this);
		this.onChange = this.onChange.bind(this);
	}

	onChangePage(evt: any, page: any) {
		this.setState({
			page
		});
	}

	onChange(event: React.ChangeEvent<any>) {
		this.setState({
			commissionType: event.target.value
		}, () => {
			const { series } = this.props;
			const filteredSeries: Series[] | null = this.state.commissionType !== ALL_COMMISSION_PAID_TYPE && series !== null ?
				series.filter((event) => event.commissionType === this.state.commissionType) : null;
			this.setState({ filteredSeries, page: 1 });
		});
	}

	onClick(evt: any) {
		const {
			series,
			translate
		} = this.props;

		xslx(series, 'Account Statement.xlsx', false, translate);
	}

	NoCommissionDataView = (responsiveClass: string, commissionType: string) => {
		const {
			classes,
			translate
		} = this.props;

		const isManualCommissionSelected: boolean = commissionType === MANUAL_COMMISSION_PAID_TYPE;
		const isAutoCommissionSelected: boolean = commissionType === AUTO_COMMISSION_PAID_TYPE;
		const allCommissionText: string = translate('series.table.empty.all.commission.text');
		const manualCommissionText: string = translate('series.table.empty.manual.commission.text');
		const autoCommissionText: string = translate('series.table.empty.auto.commission.text');
		const noCommissionText: string = translate('series.table.empty.commission.header.text', {
			commissionType: isManualCommissionSelected ? manualCommissionText : autoCommissionText
		});
		const noCommissionSubText: string = translate('series.table.empty.commission.subheader.text', {
			commissionType: isManualCommissionSelected ? manualCommissionText :
				isAutoCommissionSelected ? autoCommissionText : allCommissionText
		});

		return (
			<>
				<TableRow className={classes.blankDataTableCell}>
					<TableCell colSpan={12} className={classnames(classes.tableEmptyCell, responsiveClass)}>
						<div className={classes.emptyData}>
							<img src={EmptyCommissionIcon} alt={noCommissionText} className={classes.emptyDataIcon} />
							<div className={classes.emptyDataHeader}>
								<span>{noCommissionText}</span>
								<p>{noCommissionSubText}</p>
							</div>
						</div>
					</TableCell>
				</TableRow>
			</>);
	}

	NormalRows = (responsiveClass: string, event: Series, ignored: string[], display: Series[]) => {
		const {
			classes,
			hideEvent,
			translate,
			dateColumnCopy,
			referralsSearchEnable,
			forInvoice
		} = this.props;

		const tableDateCell: string = classnames(classes.tableCell, responsiveClass, {
			[classes.tableDateCell]: dateColumnCopy
		});

		return (
			<>
				<StyledTableRow className={responsiveClass}>
					{!hideEvent && (
						<TableCell
							data-label={dateColumnCopy ? dateColumnCopy : translate('series.table.month.label')}
							className={tableDateCell}>
							{event.event.eventLabel} {event.event.eventLabelSmall &&
								<span className={classnames(classes.tableCellSmallSpan, responsiveClass)}>
									({event.event.eventLabelSmall})
								</span>}
						</TableCell>
					)}
					{event.fields.map((column: SeriesField) => (
						!ignored.includes(column.fieldName) &&
						<TableCell data-label={column.fieldLabel} className={
							classnames(classes.tableCell, responsiveClass,
								display[0].fields.length <= 3 ? classes.smallTableCell : '',
								referralsSearchEnable || forInvoice ? classes.smallSearchTableCell : '',
								forInvoice ? classes.downloadButtonCell : ''
							)}
							key={`${event.event.eventLabel}-${column.fieldName}`}>
							<APCell field={column} />
						</TableCell>
					))}
				</StyledTableRow>
			</>
		);
	}

	render() {
		const {
			classes,
			hideEvent,
			pagination,
			series,
			translate,
			summaryHeader,
			summaryHeaderBrackets,
			dateColumnCopy,
			breakPoint,
			ignoredFields,
			referralsSearchEnable,
			referralSearchString,
			forInvoice
		} = this.props;

		const { filteredSeries } = this.state;

		if (series === null || series.length === 0) {
			return null;
		}

		const displaySeries: Array<Series> | null = filteredSeries || series;
		let display: Array<Series> = displaySeries;
		if (pagination) {
			const page = this.state.page - 1;
			if (page > 0 && displaySeries.length < 10) {
				this.setState({ page: 1 }, () => {
					const page = 0;
					display = displaySeries.slice(page * pagination, (page * pagination) + pagination);
				});
			} else {
				display = displaySeries.slice(page * pagination, (page * pagination) + pagination);
			}
		}

		const commissionAllText: string = translate('series.table.commission.dropdown.all.option');
		const commissionAutomatedText: string = translate('series.table.commission.dropdown.automated.option');
		const commissionManualText: string = translate('series.table.commission.dropdown.manual.option');

		const match = window.matchMedia(`(max-width: ${breakPoint ? breakPoint : '840'}px)`);
		const responsiveClass: string = (match.matches) ? 'isResponsive' : '';
		const summaryHeaderTextClass: string = classnames(classes.tableHeadingCell, responsiveClass, {
			[(summaryHeader === 'Transactions' ? classes.summaryTableHeaderHalf : classes.summaryTableHeaderFull)]: match.matches
		});
		const summaryHeaderTransactionLength: string = classnames(classes.tableHeadingCell, responsiveClass, {
			[classes.summaryTableTransactionText]: match.matches
		}, classes.tableHeadingCellText);
		const tableHeaderCell: string = classnames(classes.tableHeaderCell, responsiveClass, {
			[classes.smallTableHeaderCell]: display.length > 0 && display[0].fields.length <= 3,
			[classes.smallSearchTableHeaderCell]: referralsSearchEnable || forInvoice
		});
		const ignored = (ignoredFields) ? ignoredFields : [];
		const isNoCommissionDataView: boolean = !!(summaryHeader && display.length === 0);
		const commissionViewType: CommissionViewType = !isNoCommissionDataView && display[0].commissionViewType ?
			display[0].commissionViewType : COMMISSIONS_SUMMARY_VIEW as CommissionViewType;

		const hasManualCommission: Series | undefined = series.find(event => event.commissionType === MANUAL_COMMISSION_PAID_TYPE);
		const hasAutoCommission: Series | undefined = series.find(event => event.commissionType === AUTO_COMMISSION_PAID_TYPE);
		const hideCommissionTypeDropdown: boolean = !hasManualCommission || !hasAutoCommission;

		return (
			<TableContainer className={classes.tableContainer}>
				<Table stickyHeader>
					<TableHead>
						{referralsSearchEnable &&
							<TableRow>
								<TableCell
									colSpan={9}
									className={classnames(classes.tableHeadingCell,
										classes.searchTableHeadingCell, responsiveClass)}>
									<span className={classnames(classes.searchDataTableHeadingCellSpan, responsiveClass)}>
										{
											translate('referrals.search.results.label', {
												searchValue: `"${referralSearchString}"`
											})
										}
									</span>
								</TableCell>
							</TableRow>
						}
						<TableRow>
							{summaryHeader && (
								<TableCell
									colSpan={commissionViewType === COMMISSIONS_DETAILED_VIEW ? 2 :
										hideCommissionTypeDropdown ? 3 : 1} className={summaryHeaderTextClass}>
									<span className={classnames(classes.summaryHeaderTextSpan, responsiveClass)}>
										{summaryHeader} {summaryHeaderBrackets &&
											<span className={classes.tableHeadingCellSpan}>
												({summaryHeaderBrackets})
											</span>}
									</span>
								</TableCell>
							)}

							{!isNoCommissionDataView && summaryHeader === 'Transactions' && (
								<TableCell
									colSpan={hideCommissionTypeDropdown ? 7 : 3}
									className={summaryHeaderTransactionLength}>
									<span className={classnames(classes.summaryHeaderTextSpan, classes.tableHeadingCellSpan, responsiveClass)}>
										{translate('series.table.transaction.header.text', {
											displayLength: display.length,
											seriesLength: series.length
										})}
									</span>
								</TableCell>
							)}

							{summaryHeader && !hideCommissionTypeDropdown && (
								<TableCell colSpan={4} className={classnames(classes.tableDropdownCell, responsiveClass)}>
									<>
										<span className={classes.showLabel}>{translate('series.table.show.commission-dropdown-label')}</span>
										<APSelectDropdown
											name={'commissionType'}
											value={this.state.commissionType}
											data={[
												{ label: commissionAllText, value: ALL_COMMISSION_PAID_TYPE },
												{ label: commissionAutomatedText, value: AUTO_COMMISSION_PAID_TYPE },
												{ label: commissionManualText, value: MANUAL_COMMISSION_PAID_TYPE }
											]}
											onChange={this.onChange}
										/>
									</>
								</TableCell>
							)}
						</TableRow>
						{!isNoCommissionDataView && (
							<TableRow className={classnames(classes.tableRowHeader, responsiveClass)}>
								{!hideEvent && (
									<TableCell className={classnames(classes.tableHeaderCell, responsiveClass)} key={dateColumnCopy}>
										{dateColumnCopy ? dateColumnCopy : translate('series.table.month.label')}
									</TableCell>
								)}
								{display.length > 0 && display[0].fields.map(column => (
									!ignored.includes(column.fieldName) &&
									<TableCell
										className={tableHeaderCell}
										key={column.fieldName}
									>
										{column.fieldLabel}
									</TableCell>
								))}
							</TableRow>
						)}
					</TableHead>
					<TableBody>
						{display.length > 0 && display.map((event, index) => (
							<>
								{referralsSearchEnable ?
									<APTradeRows
										referralsSearchEnable={referralsSearchEnable}
										responsiveClass={responsiveClass}
										event={event}
										ignored={ignored}
										display={display}
										classnames={classnames}
										indexNumber={index}
										breakPoint={breakPoint}
										page={this.state.page}
									/>
									: this.NormalRows(responsiveClass, event, ignored, display)
								}
							</>
						))}
						{isNoCommissionDataView && (
							<>
								{this.NoCommissionDataView(responsiveClass, this.state.commissionType)}
							</>
						)}
					</TableBody>
				</Table>
				{!isNoCommissionDataView && (pagination && pagination !== 0) ? (
					<div className={classes.pagination}>
						<Pagination
							count={Math.ceil(displaySeries.length / pagination)}
							page={this.state.page}
							onChange={this.onChangePage}
						/>
					</div>
				) : ''}
			</TableContainer>
		);
	}
}

export default withTranslate(
	withStyles(APSeriesTableStyles)(APSeriesTable)
);
